import axios, { AxiosResponse } from 'axios';
import loggableProp from '../functions/loggableProp';
import { useInflightTracker } from './useInflightTracker';

export const defaultHeader = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'x-requesting-app': `wds-accounts-${process.env.REACT_APP_GIT_HASH}`,
};

export default function useAxios() {
    const tracker = useInflightTracker();
    const call = async <T=any>(method: 'GET' | 'POST', url: string, data = {}, withCredentials = false, customHeaders = {}): Promise<AxiosResponse<T> | null> => {
        if (tracker.isAlreadyInflight(url))
            return null;
        tracker.addApiCall(url);
        const headers = Object.assign({}, defaultHeader, customHeaders);
        let response = null;
        try {
            if (method === 'GET') {
                response = await axios({
                    headers,
                    method,
                    params: data,
                    url,
                    withCredentials
                });
            } else if (method === 'POST') {
                response = await axios({
                    data,
                    headers,
                    method,
                    url,
                    withCredentials
                });
            }
        } catch(error) {
            if (axios.isAxiosError(error) && error.response) {
                if (error.response.status === 403 && typeof error.response.data === 'string') {
                    // check for Akamai WAF blocking and capture reference ID
                    // NRQL: SELECT * FROM JavaScriptError WHERE errorMessage='Request blocked by Akamai WAF'
                    const akamaiReferenceId = error.response.data.match(/Reference&#32;&#35;(.*)\n/);
                    if (akamaiReferenceId) {
                        const customAttributes =  {
                            akamaiReferenceId: akamaiReferenceId[1].replace(/&#46;/g, '.'),
                            blockedUrl: error.config.url,
                            blockedIdentifier: '',
                        }
                        if (error.config.data) {
                            try {
                                const { username } = JSON.parse(error.config.data);
                                if (username) {
                                    customAttributes.blockedIdentifier = loggableProp(username);
                                }
                            } catch(e) {
                                // move on
                            }
                        }
                        if (window.newrelic) {
                            window.newrelic.noticeError(new Error('Request blocked by Akamai WAF'), customAttributes);
                        }
                        if (process.env.REACT_APP_ENV !== 'production') {
                            console.log('Request blocked by Akamai WAF', customAttributes);
                        }
                    }
                }
            }
            tracker.removeApiCall(url);
            if (axios.isAxiosError(error)) {
                return Promise.resolve(error.response as AxiosResponse<T>);
            }
            console.error(error);
        }
        tracker.removeApiCall(url);
        return response;
    };

    return {
        call,
    };
}
